import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { DateInput } from "@/components/ui/date-input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface DateFilterProps {
  onDateRangeChange: (dateType: string, startDate?: string, endDate?: string) => void;
  currentFilters: Record<string, string[]>;
}

export const DateFilter: React.FC<DateFilterProps> = ({
  onDateRangeChange,
  currentFilters,
}) => {
  const [dateType, setDateType] = useState<string>("issueDate");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Clear form when date type changes or filter is cleared externally
  useEffect(() => {
    const hasActiveFilter = currentFilters[dateType]?.length > 0;
    if (!hasActiveFilter) {
      setStartDate("");
      setEndDate("");
    }
  }, [dateType, currentFilters]);

  const handleDateTypeChange = (value: string) => {
    setDateType(value);
    // Clear existing dates when changing type
    setStartDate("");
    setEndDate("");
    // Clear any existing filter for the new type
    onDateRangeChange(value);
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (value || endDate) {
      handleApplyFilter(dateType, value || undefined, endDate || undefined);
    } else {
      onDateRangeChange(dateType);
    }
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    if (startDate || value) {
      handleApplyFilter(dateType, startDate || undefined, value || undefined);
    } else {
      onDateRangeChange(dateType);
    }
  };

  const handleApplyFilter = (type: string, start?: string, end?: string) => {
    onDateRangeChange(type, start, end);
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    onDateRangeChange(dateType);
  };

  const hasActiveFilter = currentFilters[dateType]?.length > 0;
  const hasDateInput = startDate || endDate;

  const getDateTypeLabel = (type: string) => {
    switch (type) {
      case "issueDate": return "Emissão";
      case "dueDate": return "Vencimento";
      case "paymentDate": return "Pagamento";
      default: return "Emissão";
    }
  };

  const getFilterSummary = () => {
    if (!hasActiveFilter) return null;
    
    try {
      const filterValue = currentFilters[dateType]?.[0];
      if (filterValue && filterValue.startsWith('{"type":"dateRange"')) {
        const rangeFilter = JSON.parse(filterValue);
        if (rangeFilter.start && rangeFilter.end) {
          return `${rangeFilter.start} até ${rangeFilter.end}`;
        } else if (rangeFilter.start) {
          return `a partir de ${rangeFilter.start}`;
        } else if (rangeFilter.end) {
          return `até ${rangeFilter.end}`;
        }
      }
    } catch {
      // Fallback for old format
      const filterCount = currentFilters[dateType]?.length || 0;
      if (filterCount === 1) {
        return `1 data filtrada`;
      }
      return `${filterCount} datas filtradas`;
    }
    
    return "filtro aplicado";
  };

  return (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Label htmlFor="date-type">Filtrar por:</Label>
            <Select value={dateType} onValueChange={handleDateTypeChange}>
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="issueDate">Emissão</SelectItem>
                <SelectItem value="dueDate">Vencimento</SelectItem>
                <SelectItem value="paymentDate">Pagamento</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label>De:</Label>
            <DateInput
              value={startDate}
              onChange={handleStartDateChange}
              placeholder="DD/MM/YYYY"
            />
          </div>

          <div className="flex items-center gap-2">
            <Label>Até:</Label>
            <DateInput
              value={endDate}
              onChange={handleEndDateChange}
              placeholder="DD/MM/YYYY"
            />
          </div>

          {(hasDateInput || hasActiveFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilter}
              className="text-muted-foreground hover:text-foreground"
            >
              Limpar filtro
            </Button>
          )}

          {hasActiveFilter && (
            <div className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
              {getDateTypeLabel(dateType).toLowerCase()}: {getFilterSummary()}
            </div>
          )}

          {hasActiveFilter && dateType === "paymentDate" && (
            <div className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
              Exibindo apenas lançamentos com status “Pago”
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};